import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { CONORBIT_ORG_ID } from '@/lib/constants'

export function PlatformSettingsView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [savingDefaults, setSavingDefaults] = useState(false)
  const [defaults, setDefaults] = useState<{ trial_days: string; max_journalists: string } | null>(null)

  const { data: adminProfile } = useQuery({
    queryKey: ['admin-identity'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('app_admins' as any)
        .select('first_name, last_name, email')
        .eq('user_id', user.id)
        .single() as any)
      return data as { first_name: string; last_name: string; email: string } | null
    }
  })

  const { data: platformOrg } = useQuery({
    queryKey: ['platform-org-settings'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('settings')
        .eq('id', CONORBIT_ORG_ID)
        .single() as any)
      return data as { settings: any } | null
    },
  })

  useEffect(() => {
    if (platformOrg?.settings && !defaults) {
      const s: any = platformOrg.settings
      setDefaults({
        trial_days: String(s.trial_days ?? 14),
        max_journalists: String(s.max_journalists ?? 100),
      })
    }
  }, [platformOrg, defaults])

  const saveDefaults = async () => {
    if (!defaults) return
    setSavingDefaults(true)
    try {
      const current = platformOrg?.settings ?? {}
      await (supabase
        .from('organizations')
        .update({
          settings: {
            ...current,
            trial_days: Number(defaults.trial_days),
            max_journalists: Number(defaults.max_journalists),
          }
        })
        .eq('id', CONORBIT_ORG_ID) as any)
      toast({ title: 'Platform defaults saved.' })
      qc.invalidateQueries({ queryKey: ['platform-org-settings'] })
    } catch {
      toast({ title: 'Failed to save defaults.', variant: 'destructive' })
    } finally {
      setSavingDefaults(false)
    }
  }

  const adminName = adminProfile
    ? `${adminProfile.first_name ?? ''} ${adminProfile.last_name ?? ''}`.trim()
    : '…'

  const BUILD_PHASES = [
    'Phase 0: Base schema',
    'Phase 1: Organisation',
    'Phase 2: Media Registry',
    'Phase 3: Accreditation',
    'Phase 4: Admin Shell + Events',
    'Phase 5: Stages 3-8 (Auth + Portal + Sidebar)',
    'Phase 6: Coverage Analytics + Awards',
  ]

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-[#0D2240]">Platform Settings</h2>
        <p className="text-justify text-xs text-muted-foreground mt-1">Platform-level configuration and metadata.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">CONORBIT Identity</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between py-0.5 border-b">
            <span className="text-muted-foreground">App name</span>
            <span className="font-semibold">CONORBIT</span>
          </div>
          <div className="flex justify-between py-0.5 border-b">
            <span className="text-muted-foreground">Admin email</span>
            <span className="font-mono text-xs">{adminProfile?.email ?? '…'}</span>
          </div>
          <div className="flex justify-between py-0.5">
            <span className="text-muted-foreground">Admin name</span>
            <span className="font-medium">{adminName}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Supabase Project</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <div className="flex justify-between py-0.5 border-b">
            <span className="text-muted-foreground">Project ID</span>
            <span className="font-mono text-xs">cupjjkabigqxhffunxoj</span>
          </div>
          <div className="flex justify-between py-0.5">
            <span className="text-muted-foreground">Project URL</span>
            <span className="font-mono text-xs">cupjjkabigqxhffunxoj.supabase.co</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Global Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Default Trial Length (days)</Label>
              <Input
                type="number"
                min={1}
                value={defaults?.trial_days ?? '14'}
                onChange={e => setDefaults(d => ({ ...d!, trial_days: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Max Journalists Per Org</Label>
              <Input
                type="number"
                min={1}
                value={defaults?.max_journalists ?? '100'}
                onChange={e => setDefaults(d => ({ ...d!, max_journalists: e.target.value }))}
              />
            </div>
          </div>
          <Button
            size="sm"
            onClick={saveDefaults}
            disabled={savingDefaults || !defaults}
          >
            {savingDefaults ? 'Saving…' : 'Save Defaults'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Build Status</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          {BUILD_PHASES.map((phase, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#006B3C] shrink-0" />
              <span>{phase}</span>
              <span className="ml-auto text-[#006B3C] text-xs font-medium">Complete</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
